import {
    Box,
    Link,
    IconButton,
    List,
    ListItem,
    useMediaQuery,
    useTheme,
    Button,
    Container,
    Menu
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useState, forwardRef, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser } from '../../store/userSelectors';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import { openProjectInBrowserWithToken, isDesktop, isTelegramWebApp } from '../../utils/telegramUtils'
import logo from '../../../assets/images/header/logo.svg'
import logoRu from '../../../assets/images/header/logoRu.svg'
import logoOnly from '../../../assets/images/header/logoOnly.svg'
import { useSetLangMutation } from '../../../features/user/api/userApi';
import { userApi } from '../../../features/user/api/userApi';
import { applyLanguage } from '../../utils/language';
import { getAccessToken, removeAccessToken, removeRefreshToken } from '../../utils/authStorage';
import { setUser } from '../../store/userSlice';
import MenuLink from '../ui/MenuLink';
import closeIcon from '../../../assets/images/header/close.svg';
import openIcon from '../../../assets/images/header/open.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import { paths } from '../../routes/paths';
import AuthDialog from '../auth/AuthDialog';

const Header = forwardRef((_, ref) => {
    const user = useSelector(selectCurrentUser);
    const location = useLocation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const { t, i18n } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [drawerUserOpen, setDrawerUserOpen] = useState(false);
    const [setLang] = useSetLangMutation();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(480));
    const fullLogo = i18n.language === 'ru' ? logoRu : logo;
    const logoImage = isSmallScreen ? logoOnly : fullLogo;

    const isEditor = location.pathname.startsWith('/editor');

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const changeLanguage = (lng) => {
        applyLanguage(lng);
        try {
            if (getAccessToken()) setLang(lng);
        } catch { }
    };

    const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
    const handleUserMenuClose = () => setUserMenuAnchor(null);
    const handleLogout = () => {
        try {
            removeAccessToken();
            removeRefreshToken();
        } finally {
            dispatch(setUser(null));
            handleUserMenuClose();
            window.location.reload();
        }
    };

    const navItems = false
        ? [
            { label: t('Поддержка'), href: 'https://t.me/FleepSupportBot' },
            { label: t('Тарифы'), href: 'https://fleep.bot/ru#price' },
            { label: t('Отзывы'), href: 'https://fleep.bot/ru#reviews' },
            { label: t('Блог'), href: 'https://blog.fleep.bot/' },
        ]
        : [
            { label: t('Приложения'), href: paths.projects() },
            { label: t('Поддержка'), href: 'https://t.me/FleepSupportBot' },
            { label: t('Инструкция'), href: '/instruction' },
            { label: t('Тарифы'), href: '/#pricing' },
        ];

    const handleProjectsClick = (e) => {
        if (isDesktop() && isTelegramWebApp()) {
            e.preventDefault();
            openProjectInBrowserWithToken(`${process.env.REACT_APP_DOMEN}/projects`);
        }
    };

    // Measure header height to position Drawer under the fixed header
    const headerElRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
        const el = headerElRef.current;
        if (!el) return;
        const update = () => setHeaderHeight(el.offsetHeight || 0);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!user && getAccessToken()) {
            (async () => {
                try {
                    const data = await dispatch(userApi.endpoints.getCurrentUser.initiate()).unwrap();
                    if (data) dispatch(setUser(data));
                } catch { }
            })();
        }
    }, [user, dispatch]);

    const drawerContent = (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'Roboto, sans-serif',
                '& .MuiTypography-root': { fontFamily: 'Roboto, sans-serif' },
                '& .MuiButton-root': { fontFamily: 'Roboto, sans-serif' }
            }}
        >
            {/* Drawer navigation content (no header elements inside) */}
            <Box sx={{ flex: 1, px: 1 }}>
                <Container
                    maxWidth={false}
                    disableGutters
                    sx={{
                        maxWidth: '1680px',
                        mx: 'auto',
                        [theme.breakpoints.down(1920)]: { maxWidth: '100%', mx: 0, px: '64px' },
                        [theme.breakpoints.down(1440)]: { px: '32px' },
                        [theme.breakpoints.down(1200)]: { px: '32px' },
                        [theme.breakpoints.down(992)]: { px: '32px' },
                        [theme.breakpoints.down(768)]: { px: '12px' },
                        [theme.breakpoints.down(480)]: { px: '8px' }
                    }}
                >
                    <List onClick={handleDrawerClose}>
                        {navItems.map((item) => {
                            const isExternal = /^https?:\/\//.test(item.href || '');
                            const linkProps = isExternal
                                ? { href: item.href, target: '_blank', rel: 'noopener' }
                                : { to: item.href };
                            const onClick = !isExternal && item.label === t('Приложения') ? handleProjectsClick : undefined;
                            return (
                                <ListItem
                                    key={item.label}
                                    sx={{ color: 'text.primary' }}
                                >
                                    <MenuLink {...linkProps} onClick={onClick}>{item.label}</MenuLink>
                                </ListItem>
                            );
                        })}
                        <ListItem
                            sx={{
                                color: 'text.primary',
                            }}
                        >
                            <MenuLink
                                onClick={() => changeLanguage(i18n.language === 'ru' ? 'en' : 'ru')}
                                sx={{ mr: '12.5px' }}
                            >
                                {t('EN')}
                            </MenuLink>
                        </ListItem>
                        {user ? (
                            isEditor ? null : (
                                <>
                                    <ListItem sx={{ color: 'text.primary' }}>
                                        <MenuLink
                                            onClick={(e) => { e.stopPropagation(); setDrawerUserOpen((v) => !v); }}
                                            icon={<PersonIcon fontSize='small' sx={{ color: 'inherit' }} />}
                                        >
                                            {`${user.first_name} ${user.last_name || ''}`}
                                        </MenuLink>
                                    </ListItem>
                                    {drawerUserOpen && (
                                        <>
                                            <ListItem sx={{ color: 'text.primary' }}>
                                                <MenuLink to={paths.profile()}>{t('Личный кабинет')}</MenuLink>
                                            </ListItem>
                                            <ListItem sx={{ color: 'text.primary' }}>
                                                <MenuLink
                                                    onClick={handleLogout}
                                                    icon={<LogoutIcon />}
                                                    sx={{
                                                        color: 'error.main',
                                                        '& > span:first-of-type': { width: 20, height: 20 },
                                                        '&:hover': { color: 'error.main', borderBottom: '1px solid', borderBottomColor: 'error.main' },
                                                        '&:active': { color: 'error.main', borderBottom: '1px solid', borderBottomColor: 'error.main' },
                                                    }}
                                                >
                                                    {t('Выйти')}
                                                </MenuLink>
                                            </ListItem>
                                        </>
                                    )}
                                </>
                            )
                        ) : (
                            <ListItem sx={{ color: 'text.primary' }}>
                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        handleDrawerClose();
                                        if (!location.pathname.startsWith(paths.auth())) {
                                            setLoginOpen(true);
                                        }
                                    }}
                                    sx={{
                                        fontFamily: 'Roboto, sans-serif',
                                        px: '16px',
                                        py: '14px',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        [theme.breakpoints.down(768)]: { width: '100%' }
                                    }}
                                >
                                    {t('Войти')}
                                </Button>
                            </ListItem>
                        )}
                    </List>
                </Container>
            </Box>
        </Box>
    );

    return (
        <Box
            ref={(el) => {
                headerElRef.current = el;
                if (typeof ref === 'function') ref(el);
                else if (ref) ref.current = el;
            }}
            component='header'
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1100,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: '20px',
                px: 1,
                fontFamily: 'Roboto, sans-serif',
                backgroundColor: 'background.default',
                '& .MuiTypography-root': { fontFamily: 'Roboto, sans-serif' },
                '& .MuiButton-root': { fontFamily: 'Roboto, sans-serif' },
                [theme.breakpoints.down(1200)]: {
                    py: '8px'
                },
                [theme.breakpoints.down(768)]: {
                    py: '8px'
                },
            }}
        >
            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    maxWidth: '1680px',
                    mx: 'auto',
                    [theme.breakpoints.down(1920)]: { maxWidth: '100%', mx: 0, px: '64px' },
                    [theme.breakpoints.down(1440)]: { px: '32px' },
                    [theme.breakpoints.down(1200)]: { px: '32px' },
                    [theme.breakpoints.down(992)]: { px: '32px' },
                    [theme.breakpoints.down(768)]: { px: '12px' },
                    [theme.breakpoints.down(480)]: { px: '8px' }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}
                >
                    <Link
                        component={RouterLink}
                        to={user ? paths.projects() : '/'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            component='img'
                            src={logoImage}
                            alt='Fleep Bot'
                            sx={{ height: '50px', [theme.breakpoints.down(768)]: { height: '31.42px' } }}
                        />
                    </Link>
                    {isMobile ? (
                        <>
                            <IconButton
                                edge='end'
                                onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}
                                aria-label={drawerOpen ? 'close-menu' : 'open-menu'}
                                sx={{
                                    [theme.breakpoints.down(1200)]: { py: '5px' },
                                    [theme.breakpoints.down(768)]: { py: 0 }
                                }}
                            >
                                <img src={drawerOpen ? closeIcon : openIcon} alt={drawerOpen ? 'close' : 'open'} />
                            </IconButton>
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: `${headerHeight}px`,
                                    right: 0,
                                    width: '100%',
                                    height: `calc(100vh - ${headerHeight}px)`,
                                    zIndex: 1099,
                                    bgcolor: 'background.default',
                                    transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
                                    transition: 'transform 300ms ease'
                                }}
                            >
                                {drawerContent}
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 5,
                                    alignItems: 'center'
                                }}
                            >
                                {navItems.map((item) => {
                                    const isExternal = /^https?:\/\//.test(item.href || '');
                                    const linkProps = isExternal ? { href: item.href } : { to: item.href };
                                    const handleClick = item.label === t('Приложения') ? handleProjectsClick : undefined;
                                    return (
                                        <MenuLink key={item.label} {...linkProps} onClick={handleClick}>
                                            {item.label}
                                        </MenuLink>
                                    );
                                })}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <MenuLink
                                    onClick={() => changeLanguage(i18n.language === "ru" ? "en" : "ru")}
                                    sx={{ mr: '12.5px' }}
                                >
                                    {t('EN')}
                                </MenuLink>
                                {user ? (
                                    isEditor ? null : (
                                        <MenuLink
                                            onClick={handleUserMenuOpen}
                                            icon={<PersonIcon fontSize='small' sx={{ color: 'inherit' }} />}
                                        >
                                            {`${user.first_name} ${user.last_name || ''}`}
                                        </MenuLink>
                                    )
                                ) : (
                                    <Button
                                        variant='contained'
                                        onClick={() => {
                                            if (!location.pathname.startsWith(paths.auth())) {
                                                setLoginOpen(true);
                                            }
                                        }}
                                        sx={{ px: '16px', py: '14px', fontSize: '16px', fontWeight: 400 }}
                                    >
                                        {t('Войти')}
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Container>
            <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: 'background.default',
                            ml: '12px',
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', py: '12px', px: '12px', gap: '12px', bgcolor: 'background.default' }}>
                    <MenuLink to={paths.profile()} onClick={handleUserMenuClose}>
                        {t('Личный кабинет')}
                    </MenuLink>
                    <MenuLink
                        onClick={handleLogout}
                        icon={<LogoutIcon />}
                        sx={{
                            color: 'error.main',
                            '& > span:first-of-type': { width: 20, height: 20 },
                            '&:hover': { color: 'error.main', borderBottom: '1px solid', borderBottomColor: 'error.main' },
                            '&:active': { color: 'error.main', borderBottom: '1px solid', borderBottomColor: 'error.main' },
                        }}
                    >
                        {t('Выйти')}
                    </MenuLink>
                </Box>
            </Menu>
            <AuthDialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onSuccess={async () => {
                    try {
                        const data = await dispatch(userApi.endpoints.getCurrentUser.initiate()).unwrap();
                        if (data) dispatch(setUser(data));
                    } finally {
                        setLoginOpen(false);
                    }
                }}
            />
        </Box>
    );
});

export default Header;
